/**
 * Two-entrance architecture: scaffer.co.uk (scaffolder hub) and scaffoldernearme.co.uk (customer).
 * Domain detection drives which landing/marketing content users see.
 */

import { classifyHost } from "@/lib/white-label/hosts";

export const CUSTOMER_DOMAIN =
  process.env.NEXT_PUBLIC_CUSTOMER_DOMAIN?.replace(/\/$/, "").trim() || "scaffoldernearme.co.uk";
export const SCAFFOLDER_DOMAIN =
  process.env.NEXT_PUBLIC_SCAFFOLDER_DOMAIN?.replace(/\/$/, "").trim() || "scaffer.co.uk";

export type SiteKind = "customer" | "scaffolder" | "tenant-portal";

/**
 * Detect site from request host. Localhost defaults to customer unless NEXT_PUBLIC_SITE=scaffolder.
 * Use {@link getHostnameFromRequest} in middleware for Vercel (x-forwarded-host).
 */
export function getSiteFromHost(host: string | null): SiteKind {
  const kind = classifyHost(host ?? "");
  if (kind === "platform-scaffolder" || kind === "platform-scaffolder-app") return "scaffolder";
  if (kind === "tenant-scaffer-subdomain" || kind === "tenant-custom-domain") return "tenant-portal";
  return "customer";
}

/**
 * Default post-login path for a hostname (login page callback when none is specified).
 * Customer site → homeowner/contractor dashboard; ScaffOps / tenant → workspace chooser.
 * Field-only `scaff` roles are redirected to `/app/crew` by login + middleware.
 */
export function getDefaultPostLoginPathForHost(host: string | null): string {
  const site = getSiteFromHost(host);
  if (site === "scaffolder" || site === "tenant-portal") return "/app/enter";
  return "/dashboard";
}

/** Customer site base URL (scaffoldernearme.co.uk) */
export function getCustomerSiteUrl(path = ""): string {
  const base = `https://${CUSTOMER_DOMAIN}`.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

/** Scaffolder site base URL (scaffer.co.uk) */
export function getScaffolderSiteUrl(path = ""): string {
  const base = `https://${SCAFFOLDER_DOMAIN}`.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}
